import {makeApiRequest} from './api';

export type TimeWindow = '10d' | '30d' | '90d' | '180d' | '365d' | 'all';

export type EvalProduct = 'short-term' | 'seasonal';

export interface EvalSite {
  siteId: string;
  evalSiteId: string;
  displayName: string;
  lat: number;
  lon: number;
  gaugeFeatureId?: number;
  latestMetrics?: {
    kge: number;
    crps: number;
    nrmse?: number;
  };
  // Optional org info (for global overview)
  organizationId?: string;
  organizationIdPrefix?: string;
}

export type MetricSummary = {mean: number; p10?: number; p90?: number};

export interface WindowMetrics {
  kge: MetricSummary;
  crps: MetricSummary;
  nrmse?: MetricSummary;
  coverage80?: MetricSummary;
  mae?: MetricSummary;
  percentBias?: MetricSummary;
  forecastCount: number;
  observationCount?: number;
  meanObsCoverage?: number;
}

export type SparklinePoint =
  | {date: string; value: number}
  | {t: string; v: number}
  | {x: string | number; y: number};

export interface SeasonalMetricSet {
  kge?: number | null;
  nrmse?: number | null;
  mae?: number | null;
  percentBias?: number | null;
  forecastCount?: number;
  n_valid?: number;
}

export interface SeasonalViews {
  byValidMonth?: Record<string, SeasonalMetricSet>;
  springMelt?: SeasonalMetricSet;
  byLeadHorizon?: Record<string, SeasonalMetricSet>;
  monthlyVolumeByHorizon?: Record<string, Record<string, SeasonalMetricSet>>;
}

export interface ConfigSummary {
  windows?: Record<string, WindowMetrics>;
  seasonalViews?: SeasonalViews;
}

export interface ModelSummary {
  windows: Record<string, WindowMetrics>;
  configs?: Record<string, ConfigSummary>;
  sparklineData: SparklinePoint[];
}

export interface SiteSummary {
  siteId: string;
  lastUpdated: string;
  models: Record<string, ModelSummary>;
  projectId?: string;
  // Site metadata for display
  displayName?: string;
  lat?: number;
  lon?: number;
  organizationId?: string;
}

export interface ForecastMeta {
  forecastId: string;
  model?: string;
  issuedAt?: string;
  start?: string;
  end?: string;
  [k: string]: unknown;
}

export interface ForecastEvaluation {
  siteId: string;
  forecastId: string;
  model?: string;
  issuedAt?: string;
  metrics?: Record<string, unknown>;
  [k: string]: unknown;
}

export interface Overview {
  window: string;
  sites: unknown;
  [k: string]: unknown;
}

export type EvalOverviewSiteModelStats = {
  nrmse?: number;
  kge?: number;
  crps?: number;
  mae?: number;
  percentBias?: number;
  count?: number;
  [k: string]: unknown;
};

export type EvalForecastSparklineSeries = {
  times: string[];
  values: Array<number | null>;
  [k: string]: unknown;
};

export type EvalForecastSparkline = {
  forecasts: EvalForecastSparklineSeries[];
  obs?: {times: string[]; values: Array<number | null>; [k: string]: unknown};
  [k: string]: unknown;
};

export type SeasonalIndicator = {pctDiff: number; label: string};
export type SeasonalIndicators = {
  flow?: SeasonalIndicator | null;
  swe?: SeasonalIndicator | null;
};

export type EvalOverviewSite = EvalSite & {
  models?: Record<string, EvalOverviewSiteModelStats>;
  sparkline?: EvalForecastSparkline;
  seasonalIndicators?: SeasonalIndicators;
  product?: EvalProduct;
  projectId?: string;
  skipReason?: string | null;
};

export type EvalSitesOverview = {
  site_count?: number;
  days?: number;
  sites: Array<EvalOverviewSite>;
  [k: string]: unknown;
};

export interface CustomMetricsResponse {
  forecastCount: number;
  observationCount?: number;
  meanObsCoverage?: number;
  metrics: {
    kge?: number | null;
    nrmse?: number | null;
    mae?: number | null;
    bias?: number | null;
    percentBias?: number | null;
    crps?: {mean: number | null; p10?: number | null; p90?: number | null};
    coverage80?: {mean: number | null; p10?: number | null; p90?: number | null};
  };
}

export const evalApi = {
  listSites: async (product?: EvalProduct) =>
    (await makeApiRequest<EvalSite[]>('api/v2/eval/sites', product ? {product} : {})).data,

  getSiteSummary: async (siteId: string, window: string = '30d') =>
    (
      await makeApiRequest<SiteSummary>(`api/v2/eval/sites/${siteId}/summary`, {
        window,
      })
    ).data,

  listForecasts: async (siteId: string, params?: {start?: string; end?: string; model?: string}) =>
    (await makeApiRequest<ForecastMeta[]>(`api/v2/eval/sites/${siteId}/forecasts`, params || {}))
      .data,

  getForecastEvaluation: async (siteId: string, forecastId: string) =>
    (
      await makeApiRequest<ForecastEvaluation>(
        `api/v2/eval/sites/${siteId}/forecasts/${forecastId}`
      )
    ).data,

  getOverview: async (window: string = '30d') =>
    (await makeApiRequest<Overview>('api/v2/eval/overview', {window})).data,

  getSitesOverviewGlobal: async (window: string = '30d', product?: EvalProduct) =>
    (
      await makeApiRequest<EvalSitesOverview>(
        'api/v2/eval/overview',
        {...(product ? {window, product} : {window})},
        {},
        {noContextOrg: true}
      )
    ).data,

  getCustomMetrics: async (
    siteId: string,
    params: {
      start: string;
      end: string;
      model?: string;
      lead_time_hours?: number;
      lead_time_hours_max?: number;
      valid_months?: string;
    }
  ) =>
    (await makeApiRequest<CustomMetricsResponse>(`api/v2/eval/sites/${siteId}/metrics`, params))
      .data,
};
